import type { z } from "zod";
import { ApiError, errorEnvelopeSchema } from "./errors";

/**
 * Framework-agnostic API client core (spec §4.2). Apps pass a base URL and a token getter (Supabase session);
 * every response is validated against the shared zod schema, and every error becomes an ApiError.
 */
export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken?: () => Promise<string | null>;
  apiKey?: string;
  fetchImpl?: typeof fetch;
  /** Called after every request with whether StudyWard was reachable (drives the app's offline banner). */
  onReachability?: (reachable: boolean) => void;
  /** Which app is calling; the server records it on app-only analytics events (§11.5). */
  surface?: "mobile" | "web";
}

export function createApiClient({ baseUrl, getAccessToken, apiKey, fetchImpl = fetch, onReachability, surface }: ApiClientOptions) {
  async function request<T>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (apiKey) headers.apikey = apiKey;
    if (surface) headers["x-client-surface"] = surface;
    const token = (await getAccessToken?.()) ?? apiKey;
    if (token) headers.Authorization = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetchImpl(`${baseUrl.replace(/\/$/, "")}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      onReachability?.(false);
      throw new ApiError("internal", "We couldn't reach StudyWard. Check your connection and try again.");
    }
    onReachability?.(true);

    const json: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      const parsed = errorEnvelopeSchema.safeParse(json);
      if (parsed.success) throw new ApiError(parsed.data.error.code, parsed.data.error.message, parsed.data.error.details);
      throw new ApiError("internal", "Something went wrong. Please try again.");
    }
    const parsed = schema.safeParse(json);
    if (!parsed.success) throw new ApiError("internal", "We received an unexpected response. Please try again.");
    return parsed.data;
  }

  return {
    get: <T>(path: string, schema: z.ZodType<T>) => request("GET", path, schema),
    post: <T>(path: string, schema: z.ZodType<T>, body?: unknown) => request("POST", path, schema, body),
    put: <T>(path: string, schema: z.ZodType<T>, body?: unknown) => request("PUT", path, schema, body),
    patch: <T>(path: string, schema: z.ZodType<T>, body?: unknown) => request("PATCH", path, schema, body),
    delete: <T>(path: string, schema: z.ZodType<T>) => request("DELETE", path, schema),
  };
}
export type ApiClient = ReturnType<typeof createApiClient>;
